import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import { ResendProposalInput, ResendProposalMutation } from '@/gql/graphql';

const RESEND_PROPOSAL_MUTATION = graphql(`
  mutation ResendProposal($input: ResendProposalInput!) {
    resendProposal(input: $input) {
      id
    }
  }
`);

/**
 * Re-email a sent Proposal to its recipient Contact (fixed server-side).
 * Resending clears any bounce state, so the Proposal and Project caches are
 * invalidated on success.
 */
const useResendProposal = (args?: {
  proposalId?: string;
  projectId?: string;
  options?: UseMutationOptions<
    ResendProposalMutation,
    unknown,
    ResendProposalInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: ResendProposalInput) => {
      return gqlClient.request(RESEND_PROPOSAL_MUTATION, { input });
    },
    onSuccess: (data, variables, context) => {
      if (args?.proposalId) {
        void queryClient.invalidateQueries({
          queryKey: proposalKeys.proposal(args.proposalId),
        });
      }
      if (args?.projectId) {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.project(args.projectId),
        });
      }
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useResendProposal;
